import store from "../../../store";

import onLoad from "../../../store/on-load";
import resolveRoutedVolume from "../../../store/getters/resolve/resolve-routed-volume";
import resolveRoutedSchoolLevel from "../../../store/getters/resolve/resolve-routed-school-level";
import autoVolume from "../../../store/getters/auto/auto-volume";
import autoSchoolLevel from "../../../store/getters/auto/auto-school-level";

// To be used when both volume and school level are auto values.
const redirectToAutoVolumeAndSchoolLevel = (route, next) => {
  const volume = autoVolume(store);
  const schoolLevel = autoSchoolLevel(store);

  if (volume && schoolLevel) {
    // There is a volume and school level available in the container.
    // We can use that to redirect to the current route.
    next({
      name: route.name,
      params: {
        ...route.params,
        volumeSlug: volume.slug,
        schoolLevelSlug: schoolLevel.slug,
      },
      force: true,
    });
    return;
  }

  if (volume && !schoolLevel) {
    // We have a volume but there is no school level available.
    // Redirect to the hub.
    next({
      name: "HubWithVolume",
      params: {
        volumeSlug: volume.slug,
      },
    });
    return;
  }

  // We might have a school level but there is no volume available.
  // We can only go to the root route.
  // (At this point it is irrelevant if there is or isn't
  // a schoolLevel available in the container, because there is
  // no route requiring only a school level but not a volume.)
  next({
    name: "Hub",
  });
};

// A helper function to redirect to a valid route
// when entering or updating routes in the DigibordContent component.
export default async function ensureValidVolumeAndSchoolLevel(route, next) {
  await onLoad(store);

  const routedVolume = resolveRoutedVolume(route, store);
  const routedSchoolLevel = resolveRoutedSchoolLevel(route, store);

  if (routedVolume && routedSchoolLevel) {
    // We have a volume and a school level - which is all we need. Stay here.
    next();
    return;
  }

  if (!routedVolume && !routedSchoolLevel) {
    // We have neither. Let's try to use auto values, or fall back to the hub.
    redirectToAutoVolumeAndSchoolLevel(route, next);
    return;
  }

  // We have covered cases where both slugs (volume and school level slug)
  // were valid or both were invalid. Now we need to handle situations
  // where only one of them is valid, and the other one invalid.

  // Case 1) The routed volume is valid, the routed school level is invalid.
  //         Now we might or might not be able to find a fallback school level.
  if (routedVolume && !routedSchoolLevel) {
    // First, let's see if we can have an auto school level.
    const schoolLevel = autoSchoolLevel(store);

    if (schoolLevel) {
      // We had a school level available which we can apply to the route.
      next({
        name: route.name,
        params: {
          ...route.params,
          volumeSlug: routedVolume.slug,
          schoolLevelSlug: schoolLevel.slug,
        },
        force: true,
      });
      return;
    }

    // We have a volume but there is no school level available in the container.
    // Redirect to the hub.
    next({
      name: "HubWithVolume",
      params: {
        volumeSlug: routedVolume.slug,
      },
    });
    return;
  }

  // Case 2) The routed volume is invalid, the routed school level is valid.
  //         Now we might or might not be able to find a fallback volume.

  // First, let's see if we can have an auto volume.
  const volume = autoVolume(store);

  if (volume) {
    // We had a volume available which we can apply to the route.
    next({
      name: route.name,
      params: {
        ...route.params,
        volumeSlug: volume.slug,
        schoolLevelSlug: routedSchoolLevel.slug,
      },
      force: true,
    });
    return;
  }

  // We have a volume but there is no school level available in the container.
  // Redirect to the hub.
  next({
    name: "HubWithVolume",
    params: {
      volumeSlug: routedVolume.slug,
    },
  });
}
